import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Terminal, 
  Settings, 
  CheckCircle, 
  Zap,
  Shield,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { BareMCPSuccessModalContent } from './BareMCPSuccessModal';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { RateLimitFields } from '../shared/RateLimitFields';

interface BareMCPIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integration: any) => Promise<any>;
  spaceId: string;
  socket?: SpaceSocket;
}

export default function BareMCPIntegrationModal({
  isOpen,
  onClose,
  onCreateIntegration,
  spaceId,
  socket
}: BareMCPIntegrationModalProps) {
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);
  const [integrationName, setIntegrationName] = useState('My MCP Integration');
  const [description, setDescription] = useState('Generic MCP access to my Memara memory system');
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);

  // Get or create default socket when modal opens
  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.bare_mcp);
          setSelectedSocket(defaultSocket);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);

  // Permission states
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(true);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);

  // Rate limiting
  const [requestsPerHour, setRequestsPerHour] = useState(2000);
  const [maxContentLength, setMaxContentLength] = useState(50000);

  // Check template compatibility
  const socketTemplateId = selectedSocket?.socket_config?.template_id;
  const isCompatibleTemplate = !socketTemplateId || socketTemplateId === 'mcp' || socketTemplateId === 'api';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!integrationName.trim() || !selectedSocket) return;

    // Validate template compatibility
    if (!isCompatibleTemplate) {
      alert('This socket uses an incompatible template. Please use a socket with MCP or API template.');
      return;
    }

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: '',
        excludeTagsComma: '',
        minImportance: 1,
        requestsPerHour,
      });

      const auth_config = {
        description: description.trim() || undefined,
        max_content_length: maxContentLength,
      };

      const integrationData = {
        integration_type: 'bare_mcp',
        integration_name: integrationName.trim(),
        permissions,
        auth_config,
        socket_id: selectedSocket.socket_id,
      };

      const createdIntegration = await onCreateIntegration(integrationData);
      
      setCreatedIntegration({
        integration_name: integrationData.integration_name,
        binding_id: createdIntegration.binding_id,
        auth_config: {
          ...integrationData.auth_config,
          api_key: createdIntegration.auth_config?.api_key || createdIntegration.access_token
        },
        permissions: integrationData.permissions,
        rate_limits: integrationData.permissions.rate_limits,
        max_content_length: integrationData.auth_config.max_content_length,
      });
      setShowSuccessModal(true);
      
    } catch (error: any) {
      console.error('Failed to create Bare MCP integration:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create Bare MCP integration. Please try again.';
      alert(errorMessage);
      throw error; // Re-throw to let parent handle if needed
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedIntegration(null);
    
    // Reset form
    setIntegrationName('My MCP Integration');
    setDescription('Generic MCP access to my Memara memory system');
    setCanRead(true);
    setCanWrite(true);
    setCanSearch(true);
    setCanDelete(false);
    setRequestsPerHour(2000);
    setMaxContentLength(50000);
    
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent variant="scrollable" className="max-w-5xl bg-white">
        {showSuccessModal && createdIntegration ? (
          <BareMCPSuccessModalContent
            integration={createdIntegration}
            onClose={handleSuccessModalClose}
          />
        ) : (
          <>
            <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
              <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Terminal className="w-6 h-6 text-purple-600" />
                Configure Bare MCP Integration
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                {selectedSocket ? (
                  <>Create a generic MCP server connection for any MCP-compatible client. Perfect for developers who want direct MCP access to their memory system.</>
                ) : (
                  'Setting up your integration...'
                )}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
              <DialogScrollArea className="space-y-6 px-6">
              {/* Basic Configuration */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-gray-700 text-sm font-medium">
                    Integration Name *
                  </label>
                  <Input
                    value={integrationName}
                    onChange={(e) => setIntegrationName(e.target.value)}
                    placeholder="e.g., My MCP Integration, Development MCP, Claude Desktop Access"
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-gray-700 text-sm font-medium">
                    Description
                  </label>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="What will this MCP integration be used for?"
                    className="bg-white border-gray-300 text-gray-900"
                  />
                </div>
              </div>

              {/* Features Card */}
              <Card className="border-purple-200 bg-purple-50">
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Zap className="w-4 h-4 text-purple-600" />
                    What you'll get
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">Standard MCP Tools</Badge>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">Streamable HTTP</Badge>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">Bearer Auth</Badge>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">Claude Desktop Compatible</Badge>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200">Generic Client Support</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Permissions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Permissions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <MemoryPermissionsFields
                    variant="embedded"
                    value={{
                      can_read: canRead,
                      can_write: canWrite,
                      can_search: canSearch,
                      can_delete: canDelete,
                    }}
                    onChange={(v) => {
                      setCanRead(v.can_read);
                      setCanWrite(v.can_write);
                      setCanSearch(v.can_search);
                      setCanDelete(v.can_delete);
                    }}
                  />
                </CardContent>
              </Card>

              {/* Rate Limits */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Rate Limits & Performance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <RateLimitFields
                      requestsPerHour={requestsPerHour}
                      onRequestsPerHourChange={setRequestsPerHour}
                      min={100}
                      max={10000}
                    />
                    <div className="space-y-2">
                      <label className="text-gray-700 text-sm font-medium">
                        Max Content Length (bytes)
                      </label>
                      <Input
                        type="number"
                        value={maxContentLength}
                        onChange={(e) =>
                          setMaxContentLength(parseInt(e.target.value, 10) || 50000)
                        }
                        min={1000}
                        max={100000}
                        className="bg-white border-gray-300 text-gray-900"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Advanced Socket Selection */}
              {spaceId && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <AdvancedSocketSelection
                    integration={integrationRegistry.bare_mcp}
                    spaceId={spaceId}
                    currentSocket={selectedSocket}
                    onSocketChange={setSelectedSocket}
                    disabled={isCreating || socketLoading}
                  />
                  
                  {/* Socket Loading State */}
                  {socketLoading && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                        <p className="text-blue-800 text-sm">Setting up socket...</p>
                      </div>
                    </div>
                  )}

                  {/* Socket Error State */}
                  {socketError && !socketLoading && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                        <div>
                          <p className="text-red-800 text-sm font-medium">Failed to load socket</p>
                          <p className="text-red-700 text-xs mt-1">{socketError}</p>
                          <p className="text-red-700 text-xs mt-1">You can create a socket manually in the advanced section above.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Socket Info Display */}
                  {selectedSocket && !socketLoading && (
                    <>
                      {/* Template compatibility warning */}
                      {!isCompatibleTemplate && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                          <div className="flex items-start space-x-2">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-amber-900 font-medium text-sm">Template Compatibility Issue</h4>
                              <p className="text-amber-700 text-sm mt-1">
                                This socket uses the <span className="font-medium">{socketTemplateId}</span> template, which is not optimized for MCP integrations. 
                                Consider creating a new socket with the <span className="font-medium">MCP Integration</span> template for better compatibility.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Template compatibility success */}
                      {isCompatibleTemplate && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                          <div className="flex items-start space-x-2">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-green-900 font-medium text-sm">Perfect Template Match</h4>
                              <p className="text-green-700 text-sm mt-1">
                                This socket uses the {socketTemplateId === 'mcp' ? 'MCP Integration' : 'API'} template, which is optimized for MCP integrations.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              </DialogScrollArea>
              <DialogFooter variant="scrollable" className="gap-3 px-6 pb-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isCreating}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating || !integrationName.trim() || !selectedSocket || socketLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {isCreating ? (
                    <>
                      <Settings className="w-4 h-4 mr-2 animate-spin" />
                      Creating Integration...
                    </>
                  ) : (
                    'Create MCP Integration'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
} 